const SUPABASE_PUBLIC_OBJECT_PATH = '/storage/v1/object/public/';
const SUPABASE_PUBLIC_RENDER_PATH = '/storage/v1/render/image/public/';

function validDimension(value) {
  const dimension = Number.parseInt(String(value), 10);
  return Number.isInteger(dimension) && dimension > 0 && dimension <= 2500 ? dimension : null;
}

/**
 * Builds a Supabase Storage transformation URL only for public Supabase assets.
 * Other origins are returned unchanged, so externally managed images continue
 * to work without being proxied by the application.
 */
/**
 * @param {string} source
 * @param {{ width?: number, height?: number, quality?: number, resize?: 'cover' | 'contain' | 'fill' }} [options]
 */
export function optimizeImageUrl(source, options = {}) {
  const { width, height, quality = 75, resize = 'cover' } = options;
  if (!source) return source;

  try {
    const url = new URL(source);
    if (!url.hostname.endsWith('.supabase.co') || !url.pathname.includes(SUPABASE_PUBLIC_OBJECT_PATH)) return source;

    url.pathname = url.pathname.replace(SUPABASE_PUBLIC_OBJECT_PATH, SUPABASE_PUBLIC_RENDER_PATH);
    const safeWidth = validDimension(width);
    const safeHeight = validDimension(height);
    if (safeWidth) url.searchParams.set('width', String(safeWidth));
    if (safeHeight) url.searchParams.set('height', String(safeHeight));
    url.searchParams.set('quality', String(Math.min(100, Math.max(20, Number.parseInt(String(quality), 10) || 75))));
    if (resize && ['cover', 'contain', 'fill'].includes(resize)) url.searchParams.set('resize', resize);
    return url.toString();
  } catch {
    return source;
  }
}

/**
 * @param {string} source
 * @param {number[]} widths
 * @param {{ height?: number, quality?: number, resize?: 'cover' | 'contain' | 'fill' }} [options]
 */
export function optimizeImageSrcSet(source, widths, options = {}) {
  if (!Array.isArray(widths)) return undefined;
  const entries = widths
    .map(width => validDimension(width))
    .filter(Boolean)
    .map(width => `${optimizeImageUrl(source, { ...options, width })} ${width}w`);
  return entries.length ? entries.join(', ') : undefined;
}

export function restoreOriginalImage(event, originalSource) {
  const image = event.currentTarget;
  if (!originalSource || image.dataset.originalRestored === 'true') return;
  image.dataset.originalRestored = 'true';
  image.removeAttribute('srcset');
  image.removeAttribute('sizes');
  image.src = originalSource;
}
